import * as Dialog from '@radix-ui/react-dialog';
import { Check, Copy, Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { FormSection, RequiredFieldsNote } from '@/components/ui/form-section';
import {
  useCreateEmployee,
  useCreateSkill,
  useEmployeeFormOptions,
  useSetEmployeeActive,
  useUpdateEmployee,
} from '@/hooks/employees';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import { centsToInput, parseMoneyInput } from '@shared/money';
import type { CreatedEmployee, EmployeeAdminView } from '@shared/schemas/employee';

/**
 * Add or edit an employee.
 *
 * Creating one **creates their login too** — there is no public sign-up, so this
 * is the only way a cleaner gets into the system. The password is generated on
 * the server and shown once, which is why this dialog has a second state after
 * a successful create rather than just closing.
 */
export function EmployeeFormDialog({
  open,
  onOpenChange,
  employee,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee?: EmployeeAdminView;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <EmployeeForm
            employee={employee}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function EmployeeForm({ employee, onDone }: { employee?: EmployeeAdminView; onDone: () => void }) {
  const toast = useToast();
  const isEdit = employee !== undefined;
  const options = useEmployeeFormOptions();

  const [firstName, setFirstName] = useState(employee?.firstName ?? '');
  const [lastName, setLastName] = useState(employee?.lastName ?? '');
  const [phone, setPhone] = useState(employee?.phone ?? '');
  const [email, setEmail] = useState(employee?.email ?? '');
  const [username, setUsername] = useState(employee?.username ?? '');
  const [recoveryEmail, setRecoveryEmail] = useState(employee?.recoveryEmail ?? '');
  const [address, setAddress] = useState(employee?.address ?? '');
  const [city, setCity] = useState(employee?.city ?? '');
  const [state, setState] = useState(employee?.state ?? '');
  const [zip, setZip] = useState(employee?.zip ?? '');
  const [maxJobsPerDay, setMaxJobsPerDay] = useState(String(employee?.maxJobsPerDay ?? 3));
  const [rate, setRate] = useState(centsToInput(employee?.hourlyRateCents ?? null));
  const [positionId, setPositionId] = useState(employee?.positionId ?? '');
  const [hireDate, setHireDate] = useState(employee?.hireDate ?? '');
  const [skillIds, setSkillIds] = useState<string[]>(
    (employee?.skills ?? []).map((entry) => entry.id),
  );
  const [rateError, setRateError] = useState<string | undefined>(undefined);
  const [newSkill, setNewSkill] = useState('');

  /** Set once, after a successful create. The only time this password exists. */
  const [handover, setHandover] = useState<CreatedEmployee | null>(null);

  const create = useCreateEmployee();
  const update = useUpdateEmployee(employee?.id ?? '');
  const setActive = useSetEmployeeActive(employee?.id ?? '');
  const addSkill = useCreateSkill();
  const mutation = isEdit ? update : create;

  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  if (handover) {
    return <PasswordHandover created={handover} onDone={onDone} />;
  }

  function toggleSkill(id: string) {
    setSkillIds((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );
  }

  function submit(event: FormEvent) {
    event.preventDefault();

    const money = parseMoneyInput(rate);
    if (!money.ok) {
      setRateError(money.message);
      return;
    }
    setRateError(undefined);

    const shared = {
      firstName,
      lastName,
      phone,
      address,
      city,
      state,
      zip,
      maxJobsPerDay: Number(maxJobsPerDay) || 0,
      hourlyRateCents: money.cents,
      positionId: positionId === '' ? null : positionId,
      hireDate: hireDate === '' ? null : hireDate,
      skillIds,
    };

    if (isEdit) {
      update.mutate(shared, {
        onSuccess: (saved) => {
          toast.success(`Saved ${saved.fullName}.`);
          onDone();
        },
      });
      return;
    }

    create.mutate(
      {
        ...shared,
        ...(email.trim() === '' ? {} : { email: email.trim() }),
        ...(username.trim() === '' ? {} : { username: username.trim() }),
        ...(recoveryEmail.trim() === '' ? {} : { recoveryEmail: recoveryEmail.trim() }),
      },
      {
        // Not a toast and not a close — the password is on this screen and
        // nowhere else, so the dialog stays open until it has been taken.
        onSuccess: setHandover,
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {isEdit ? `Edit ${employee.fullName}` : 'Add an employee'}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        {isEdit
          ? 'Their login cannot be changed here — that is deliberate.'
          : 'This creates their login as well, so they can sign in to the employee portal.'}
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-6">
        <RequiredFieldsNote />

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <FormSection title="Who they are">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="First name"
              name="firstName"
              required
              value={firstName}
              error={fieldErrors.firstName}
              onChange={(event) => {
                setFirstName(event.target.value);
              }}
            />
            <Field
              label="Last name"
              name="lastName"
              required
              value={lastName}
              error={fieldErrors.lastName}
              onChange={(event) => {
                setLastName(event.target.value);
              }}
            />
          </div>
          <Field
            label="Phone"
            name="phone"
            type="tel"
            value={phone}
            error={fieldErrors.phone}
            onChange={(event) => {
              setPhone(event.target.value);
            }}
          />
        </FormSection>

        {isEdit ? (
          <FormSection title="How they sign in">
            <p className="text-body text-ink-700">
              {employee.email ?? employee.username ?? 'No login'}
              <span className="ml-2 rounded-full bg-ink-100 px-2 py-0.5 text-meta text-ink-700">
                Cannot be changed
              </span>
            </p>
            <p className="text-meta text-ink-500">
              A login that moves is one that password resets and the audit trail stop agreeing
              about. Changing it is a separate, deliberate act.
            </p>
          </FormSection>
        ) : (
          <FormSection
            title="How they will sign in"
            description="An email address, or a username for someone who has no work email. Either works — the second is why username sign-in exists."
          >
            <Field
              label="Email"
              name="email"
              type="email"
              value={email}
              error={fieldErrors.email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
            <Field
              label="Username"
              name="username"
              value={username}
              error={fieldErrors.username}
              onChange={(event) => {
                setUsername(event.target.value);
              }}
              hint="Letters, numbers, dots and underscores."
            />
            <Field
              label="Recovery email"
              name="recoveryEmail"
              type="email"
              value={recoveryEmail}
              error={fieldErrors.recoveryEmail}
              onChange={(event) => {
                setRecoveryEmail(event.target.value);
              }}
              hint="Required if they have no email of their own — without one, nobody can ever reset this account or contact them."
            />
          </FormSection>
        )}

        <FormSection title="The work">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="employee-position">Position</Label>
              <select
                id="employee-position"
                name="positionId"
                value={positionId}
                onChange={(event) => {
                  setPositionId(event.target.value);
                }}
                className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
              >
                <option value="">Not set</option>
                {(options.data?.positions ?? []).map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
              </select>
            </div>

            <Field
              label="Hire date"
              name="hireDate"
              type="date"
              value={hireDate}
              error={fieldErrors.hireDate}
              onChange={(event) => {
                setHireDate(event.target.value);
              }}
            />

            <Field
              label="Max jobs a day"
              name="maxJobsPerDay"
              type="number"
              min={1}
              max={20}
              required
              value={maxJobsPerDay}
              error={fieldErrors.maxJobsPerDay}
              onChange={(event) => {
                setMaxJobsPerDay(event.target.value);
              }}
              hint="Scheduling will not assign more than this in one day."
            />

            <Field
              label="Hourly rate"
              name="hourlyRate"
              inputMode="decimal"
              value={rate}
              error={rateError ?? fieldErrors.hourlyRateCents}
              onChange={(event) => {
                setRate(event.target.value);
                setRateError(undefined);
              }}
              placeholder="18.00"
              hint="Admin only. Nobody but this person ever sees their own rate, and nobody sees anyone else's."
            />
          </div>

          <fieldset>
            <legend className="text-label text-ink-900">Skills</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {(options.data?.skills ?? []).map((entry) => {
                const selected = skillIds.includes(entry.id);
                return (
                  <button
                    key={entry.id}
                    type="button"
                    role="checkbox"
                    aria-checked={selected}
                    onClick={() => {
                      toggleSkill(entry.id);
                    }}
                    className={cn(
                      'inline-flex min-h-touch items-center rounded-full px-3.5 text-label',
                      selected
                        ? 'bg-honey-400 text-ink-900'
                        : 'border border-ink-300 bg-white text-ink-700 hover:bg-honey-50',
                    )}
                  >
                    {entry.name}
                  </button>
                );
              })}
              {(options.data?.skills ?? []).length === 0 ? (
                <p className="text-meta text-ink-500">
                  No skills recorded yet. Add the first one below.
                </p>
              ) : null}
            </div>

            {/*
              Skills are created here rather than on their own screen. Nothing
              seeded a taxonomy — data-model.md only gives examples — so the list
              is built by whoever knows what is worth recording, at the moment
              they need it.
            */}
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <Field
                label="Add a skill"
                name="newSkill"
                value={newSkill}
                className="sm:w-56"
                onChange={(event) => {
                  setNewSkill(event.target.value);
                }}
                placeholder="Carpet extraction"
              />
              <Button
                variant="outline"
                disabled={addSkill.isPending || newSkill.trim().length === 0}
                onClick={() => {
                  addSkill.mutate(newSkill.trim(), {
                    onSuccess: (created) => {
                      setSkillIds((current) => [...current, created.id]);
                      setNewSkill('');
                    },
                    onError: (cause) => {
                      toast.error(
                        cause instanceof ApiError
                          ? cause.message
                          : 'That skill could not be added.',
                      );
                    },
                  });
                }}
              >
                <Plus aria-hidden="true" />
                Add
              </Button>
            </div>
          </fieldset>
        </FormSection>

        <FormSection
          title="Home address"
          description="Admin only. Here so payroll and mileage have somewhere to look — it is never shown in the employee or client portals."
        >
          <Field
            label="Street address"
            name="address"
            value={address}
            error={fieldErrors.address}
            onChange={(event) => {
              setAddress(event.target.value);
            }}
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="City"
              name="city"
              value={city}
              onChange={(event) => {
                setCity(event.target.value);
              }}
            />
            <Field
              label="State"
              name="state"
              value={state}
              onChange={(event) => {
                setState(event.target.value);
              }}
            />
            <Field
              label="ZIP"
              name="zip"
              value={zip}
              onChange={(event) => {
                setZip(event.target.value);
              }}
            />
          </div>
        </FormSection>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {isEdit ? (
            <Button
              variant={employee.isActive ? 'danger' : 'outline'}
              className="sm:mr-auto"
              disabled={setActive.isPending}
              onClick={() => {
                setActive.mutate(!employee.isActive, {
                  onSuccess: (saved) => {
                    toast.success(
                      saved.isActive
                        ? `${saved.fullName} is active again.`
                        : // Says both halves, because it does both halves.
                          `${saved.fullName} deactivated and signed out everywhere.`,
                    );
                    onDone();
                  },
                  onError: (cause) => {
                    toast.error(
                      cause instanceof ApiError ? cause.message : 'That could not be saved.',
                    );
                  },
                });
              }}
            >
              {employee.isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
          ) : null}

          <Dialog.Close asChild>
            <Button variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add employee'}
          </Button>
        </div>
      </form>
    </>
  );
}

/**
 * The one-time password, after a successful create.
 *
 * A separate screen rather than a toast, because it cannot be retrieved: it was
 * generated on the server, hashed, and the readable form exists only in this
 * response. Closing this dialog loses it, and the only recovery is a password
 * reset — which, for a cleaner with no email, means an admin doing it for them.
 */
function PasswordHandover({ created, onDone }: { created: CreatedEmployee; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const signInWith = created.employee.email ?? created.employee.username ?? '';

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {created.employee.fullName} is set up
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        Give them these details over something private. The password is shown once and is not stored
        anywhere it can be read back.
      </Dialog.Description>

      <dl className="mt-5 space-y-3 rounded-sm border border-ink-300 bg-ink-100/40 p-4">
        <div>
          <dt className="text-meta text-ink-500">They sign in with</dt>
          <dd className="text-body text-ink-900">{signInWith}</dd>
        </div>
        <div>
          <dt className="text-meta text-ink-500">Temporary password</dt>
          <dd className="mt-1 flex flex-wrap items-center gap-2">
            <code className="min-w-0 break-all rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-body">
              {created.temporaryPassword}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void navigator.clipboard.writeText(created.temporaryPassword).then(() => {
                  setCopied(true);
                });
              }}
            >
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </dd>
        </div>
      </dl>

      <p className="mt-3 text-meta text-ink-500">
        Ask them to change it after their first sign-in.
      </p>

      <div className="mt-6 flex justify-end">
        <Button onClick={onDone}>I have taken a copy</Button>
      </div>
    </>
  );
}
